import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileLock2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SmileVerifyCard } from './estate-vault/SmileVerifyCard';
import {
  ClaimLoadingScreen, ClaimErrorScreen, ClaimThankYouScreen,
} from './estate-vault/SuccessorClaimStates';
import {
  InfoHint, PreviewBanner, QuorumContextBanner, DocUploadCard,
} from './estate-vault/SuccessorClaimSections';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Upload page — reached via one-time link emailed by MIF admins
// ---------------------------------------------------------------------------
export const SuccessorClaimUpload = () => {
  const { token } = useParams();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState(null); // doc_type currently uploading
  const [narrative, setNarrative] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/successor/claim/${token}`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Link error (${res.status})`);
      }
      setClaim(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const handleFileChange = (docType, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File exceeds the 8 MB limit.'); return;
    }
    setFiles((f) => ({ ...f, [docType]: file }));
  };

  const handleUpload = async (docType) => {
    const file = files[docType];
    if (!file) { toast.error('Please choose a file first.'); return; }
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append('doc_type', docType);
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/successor/claim/${token}/upload`, {
        method: 'POST', body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Upload failed.');
      }
      toast.success('Uploaded.');
      await reload();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(null); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/successor/claim/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ narrative: narrative.trim() || null }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Submit failed.');
      }
      setSubmitted(true);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  if (loading) return <ClaimLoadingScreen />;
  if (error) return <ClaimErrorScreen error={error} onReturn={() => navigate('/')} />;
  if (submitted) return <ClaimThankYouScreen />;

  const uploaded = new Set(claim.uploads_completed || []);
  const smileSupported = !!claim.smile_id_supported;
  const otherDocs = (claim.required_documents || []).filter((d) => (smileSupported ? d.type !== 'gov_id' : true));
  const allUploaded = uploaded.has('gov_id') && uploaded.has('selfie')
    && otherDocs.filter((d) => d.type !== 'gov_id' && d.type !== 'selfie').every((d) => uploaded.has(d.type));
  const narrativeRemaining = (claim.narrative_max_chars || 2000) - narrative.length;

  return (
    <div className="min-h-screen bg-linear-to-br from-emerald-50 via-white to-slate-50 px-4 py-12">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-10" data-testid="succ-claim-upload">
        <div className="flex items-center gap-2 mb-3">
          <FileLock2 className="w-7 h-7 text-emerald-700" />
          <h1 className="text-2xl font-bold text-slate-900">Upload your verification documents</h1>
        </div>
        {claim.preview && <PreviewBanner />}
        {claim.member_display_name && claim.quorum_required && (
          <QuorumContextBanner
            memberName={claim.member_display_name}
            quorumRequired={claim.quorum_required}
            quorumVerifiedCount={claim.quorum_verified_count}
          />
        )}
        <p className="text-sm text-slate-600 leading-relaxed mb-6">
          We need a few documents to confirm your identity and to make sure you are the person the member listed. All files are encrypted as soon as they reach us, and only two senior members of our team may unlock them &mdash; both must agree before any information is shared with you.
        </p>

        {smileSupported && (
          <SmileVerifyCard
            token={token}
            idDone={uploaded.has('gov_id')}
            selfieDone={uploaded.has('selfie')}
            smileSupported={smileSupported}
            onUploaded={reload}
          />
        )}

        {otherDocs.map((doc) => (
          <DocUploadCard
            key={doc.type}
            doc={doc}
            done={uploaded.has(doc.type)}
            file={files[doc.type]}
            uploading={uploading}
            onFileChange={handleFileChange}
            onUpload={handleUpload}
          />
        ))}

        <div className="mt-6">
          <div className="flex items-center">
            <Label htmlFor="succ-narrative" className="text-sm">Anything else our admin team should know?</Label>
            <InfoHint testId="succ-tip-narrative" text="Optional. Helps our admin team understand context. Not shared with anyone outside MIF." />
          </div>
          <Textarea
            id="succ-narrative"
            rows={4}
            value={narrative}
            maxLength={claim.narrative_max_chars || 2000}
            onChange={(e) => setNarrative(e.target.value)}
            placeholder="e.g. The member's situation, contact times, and any reference numbers we should quote. Please share whatever you think will help us confirm what has happened."
            data-testid="succ-narrative"
          />
          <p className={`text-xs mt-1 ${narrativeRemaining < 100 ? 'text-amber-700' : 'text-slate-500'}`} data-testid="succ-narrative-count">
            {narrativeRemaining} of {claim.narrative_max_chars || 2000} characters remaining
          </p>
        </div>

        <div className="mt-6 border-t border-slate-200 pt-4">
          <Button onClick={handleSubmit} disabled={!allUploaded || submitting}
            className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="succ-submit-claim">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting</> : 'Send for review'}
          </Button>
          <p className="text-xs text-slate-500 mt-2 text-center">
            {!allUploaded
              ? 'Please upload every required document to enable submission.'
              : 'Two members of our team will independently review before anything is shared. We aim to respond within 5 business days.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuccessorClaimUpload;
